import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { filter, take } from 'rxjs';
import { IProjectModel } from '../../../models/project/project.model';
import { ProjectStore } from '../../../services/stores/projects.store';
import { DashboardHelper } from '../../../services/dashboard.service';
import { WorkspaceStore } from '../../../services/stores/workspace.store';
import { ExternalLinkStore } from '../../../services/stores/external-link.store';
import { IExternalLink } from '../../../models/child-application/externallink.model';
import { BaseComponent } from '../../base/base.component';
import * as AppConstants from '../../../modules/catch-core/data/constants/app.constants';
import * as PermissionConstants from '../../../modules/catch-core/data/constants/permission.constants';
import { PermissionService } from '../../../services/permission.service';

@Component({
  selector: 'app-project-selector',
  templateUrl: './project-selector.component.html',
  styleUrl: './project-selector.component.scss',
  standalone: false,
})
export class ProjectSelectorComponent extends BaseComponent implements OnInit {
  public projects: IProjectModel[] = [];
  public selectedProject: IProjectModel = {} as IProjectModel;
  private reportApp: IExternalLink | null = null;

  constructor(
    private projectStore: ProjectStore,
    private router: Router,
    private dashboardHelper: DashboardHelper,
    private workspaceStore: WorkspaceStore,
    private externalLinkStore: ExternalLinkStore,
    private cdr: ChangeDetectorRef,
    private permissionService: PermissionService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.subscribers.projects = this.projectStore.$projects.subscribe((projects) => {
      if (projects) {
        this.projects = projects.toArray().filter((p) => p?.id && this.permissionService.hasPermission(PermissionConstants.PROJECT_READ, p.id));
        const currentProjectId = this.selectedProject?.id;
        if (currentProjectId) {
          const projectInList = this.projects.find((p) => p.id === currentProjectId);
          if (projectInList) {
            this.selectedProject = projectInList;
            this.cdr.detectChanges();
          }
        }
      }
    });

    this.subscribers.selectedProject = this.projectStore.$selectedProject.subscribe((selProject) => {
      if (selProject) {
        const projectInList = this.projects.find((p) => p.id === selProject.id);
        if (projectInList) {
          this.selectedProject = projectInList;
          this.cdr.detectChanges();
        } else if (selProject?.id) {
          this.selectedProject = selProject;
          if (!this.projects.some((p) => p.id === selProject.id)) {
            this.projects = [selProject, ...this.projects];
          }
          this.cdr.detectChanges();
        }
      }
    });

    this.subscribers.firstExternalLink = this.externalLinkStore.$firstExternalLink.subscribe((g) => {
      if (g?.externalLinks) {
        this.reportApp =
          g.externalLinks.find((el) => el.productType?.toLowerCase() === AppConstants.CATCH_INTERNAL_APP_REPORT_LOWER) ?? null;
      }
    });
  }

  public onProjectSelected(id: string) {
    if (id === undefined || id === null || id.length === 0) {
      return;
    }
    if (!this.permissionService.hasPermission(PermissionConstants.PROJECT_READ, id)) {
      return;
    }

    this.dashboardHelper.setCurrentProject(id);
    this.dashboardHelper.setSelectedItem('');
    this.workspaceStore.searchFilter = null;
    this.dashboardHelper.setShowLeftSidePanel(true);

    if (this.reportApp) {
      this.dashboardHelper.currentETApplication = this.reportApp;
      this.workspaceStore.isRollup = true;
    }

    // Navigate only once loadProject actually resolves for this id - if it 403s,
    // ProjectStore's error handler redirects to the dashboard instead, so we never
    // navigate into a view for a project that failed to load.
    if (this.subscribers.projectLoaded) {
      this.subscribers.projectLoaded.unsubscribe();
    }
    this.subscribers.projectLoaded = this.projectStore.$selectedProject.pipe(
      filter((project) => !!project?.id && project.id === id),
      take(1),
    ).subscribe(() => {
      this.router.navigate([
        '/',
        'dashboard',
        {
          outlets: {
            leftSidePanel: ['navigate', id, '', false],
            mainPanel: [AppConstants.CATCH_INTERNAL_APP_SMARTREPORT_LOWER],
          },
        },
      ]);
    });

    this.projectStore.loadProject(id, false);
  }
}
